// ROIAnalysis.js
import React, { useState, useEffect, useRef } from 'react';
import './ROIAnalysis.css';

function ROIAnalysis({ stockData, supplierConfig, fbaTransferDelay, supplierTransitDelay }) {
  // États pour les paramètres globaux
  const [timeSettingsFBM, setTimeSettingsFBM] = useState(300); // minutes pour 1000 unités
  const [timeSettingsFBA, setTimeSettingsFBA] = useState(30);  // minutes pour 1000 unités
  const [fbaVolumeMultiplier, setFbaVolumeMultiplier] = useState(3.0); // multiplicateur de volume FBA
  
  // États pour la calculatrice de test
  const [testPurchasePrice, setTestPurchasePrice] = useState(0.75);
  const [testSellingPriceFBM, setTestSellingPriceFBM] = useState(3.99);
  const [testSellingPriceFBA, setTestSellingPriceFBA] = useState(4.99);
  
  // États pour les frais configurables
  const [feeSettings, setFeeSettings] = useState({
    amazonCommission: 15, // %
    fbaFees: 2.20, // € par unité
    fbmShipping: 1.18, // € par unité
    digitalServicesFee: 3, // % sur (commission + frais FBA)
    tvaRate: 20, // % TVA
    additionalFees: [] // frais additionnels configurables
  });
  
  // États pour les prix d'achat, prix de vente et données calculées
  const [purchasePrices, setPurchasePrices] = useState({});
  const [sellingPricesFBM, setSellingPricesFBM] = useState({});
  const [sellingPricesFBA, setSellingPricesFBA] = useState({});
  const [roiData, setRoiData] = useState([]);
  const [sortField, setSortField] = useState('roiAnnualFBM');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pour gérer le délai de recalcul
  const recalcTimer = useRef(null);
  
  // Paramètres de stock (identiques à ReorderRecommendations)
  const safetyStock = 8; // jours

  // Charger les données depuis l'API
  useEffect(() => {
    loadROIData();
  }, []);

  // Charger les prix depuis l'API
  const loadPricesFromAPI = async () => {
    try {
      const response = await fetch('/api/prices');
      if (response.ok) {
        const allPrices = await response.json();
        
        const purchasePricesData = {};
        const sellingFBMPricesData = {};
        const sellingFBAPricesData = {};
        
        allPrices.forEach(price => {
          if (price.price_type === 'purchase') {
            purchasePricesData[price.sku] = parseFloat(price.price) || 0;
          } else if (price.price_type === 'selling_fbm') {
            sellingFBMPricesData[price.sku] = parseFloat(price.price) || 0;
          } else if (price.price_type === 'selling_fba') {
            sellingFBAPricesData[price.sku] = parseFloat(price.price) || 0;
          }
        });
        
        return { purchasePricesData, sellingFBMPricesData, sellingFBAPricesData };
      }
    } catch (error) {
      console.error('Erreur lors du chargement des prix ROI depuis l\'API:', error);
    }
    return { purchasePricesData: {}, sellingFBMPricesData: {}, sellingFBAPricesData: {} };
  };

  // Charger les paramètres depuis l'API
  const loadSettingsFromAPI = async () => {
    try {
      const response = await fetch('/api/roi/settings');
      if (response.ok) {
        const settings = await response.json();
        
        // Reconstituer les objets de configuration
        const timeSettings = {};
        const feeSettingsData = { ...feeSettings }; // Valeurs par défaut
        
        Object.entries(settings).forEach(([key, value]) => {
          try {
            const parsedValue = JSON.parse(value);
            if (key === 'timeSettings') {
              Object.assign(timeSettings, parsedValue);
            } else if (key === 'feeSettings') {
              Object.assign(feeSettingsData, parsedValue);
            }
          } catch (e) {
            // Si pas JSON, traiter comme string/number
            if (key.includes('time')) {
              timeSettings[key] = parseFloat(value) || 0;
            }
          }
        });
        
        return { timeSettings, feeSettingsData };
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres ROI depuis l\'API:', error);
    }
    return { timeSettings: {}, feeSettingsData: feeSettings };
  };

  // Migrer les données depuis localStorage vers API (one-time migration)
  const migrateLocalStorageData = async () => {
    const savedPrices = localStorage.getItem('roiPurchasePrices');
    const savedSellingPricesFBM = localStorage.getItem('roiSellingPricesFBM');
    const savedSellingPricesFBA = localStorage.getItem('roiSellingPricesFBA');
    const savedTimeSettings = localStorage.getItem('roiTimeSettings');
    const savedFeeSettings = localStorage.getItem('roiFeeSettings');
    
    let migrated = 0;
    
    // Migrer prix d'achat
    if (savedPrices) {
      try {
        const prices = JSON.parse(savedPrices);
        for (const [sku, price] of Object.entries(prices)) {
          if (price > 0) {
            await fetch(`/api/prices/${sku}/purchase`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ price: parseFloat(price) }),
            });
            migrated++;
          }
        }
      } catch (e) {
        console.error('Erreur migration prix d\'achat:', e);
      }
    }
    
    // Migrer prix FBM
    if (savedSellingPricesFBM) {
      try {
        const prices = JSON.parse(savedSellingPricesFBM);
        for (const [sku, price] of Object.entries(prices)) {
          if (price > 0) {
            await fetch(`/api/prices/${sku}/selling_fbm`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ price: parseFloat(price) }),
            });
            migrated++;
          }
        }
      } catch (e) {
        console.error('Erreur migration prix FBM:', e);
      }
    }
    
    // Migrer prix FBA
    if (savedSellingPricesFBA) {
      try {
        const prices = JSON.parse(savedSellingPricesFBA);
        for (const [sku, price] of Object.entries(prices)) {
          if (price > 0) {
            await fetch(`/api/prices/${sku}/selling_fba`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ price: parseFloat(price) }),
            });
            migrated++;
          }
        }
      } catch (e) {
        console.error('Erreur migration prix FBA:', e);
      }
    }
    
    // Migrer paramètres temps
    if (savedTimeSettings) {
      try {
        const timeSettings = JSON.parse(savedTimeSettings);
        await fetch('/api/roi/settings', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timeSettings: JSON.stringify(timeSettings)
          }),
        });
        migrated++;
      } catch (e) {
        console.error('Erreur migration temps:', e);
      }
    }
    
    // Migrer paramètres frais
    if (savedFeeSettings) {
      try {
        const feeSettings = JSON.parse(savedFeeSettings);
        await fetch('/api/roi/settings', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            feeSettings: JSON.stringify(feeSettings)
          }),
        });
        migrated++;
      } catch (e) {
        console.error('Erreur migration frais:', e);
      }
    }

    if (migrated > 0) {
      console.log(`Migration ROI réussie: ${migrated} éléments migrés vers la base de données`);
      // Supprimer localStorage après migration réussie
      localStorage.removeItem('roiPurchasePrices');
      localStorage.removeItem('roiSellingPricesFBM');
      localStorage.removeItem('roiSellingPricesFBA');
      localStorage.removeItem('roiTimeSettings');
      localStorage.removeItem('roiFeeSettings');
    }
  };

  // Charger toutes les données ROI
  const loadROIData = async () => {
    // Migration one-time des données localStorage vers API
    await migrateLocalStorageData();
    
    // Charger les prix depuis l'API
    const { purchasePricesData, sellingFBMPricesData, sellingFBAPricesData } = await loadPricesFromAPI();
    setPurchasePrices(purchasePricesData);
    setSellingPricesFBM(sellingFBMPricesData);
    setSellingPricesFBA(sellingFBAPricesData);
    
    // Charger les paramètres depuis l'API
    const { timeSettings, feeSettingsData } = await loadSettingsFromAPI();
    
    if (timeSettings.fbm) setTimeSettingsFBM(timeSettings.fbm);
    if (timeSettings.fba) setTimeSettingsFBA(timeSettings.fba);
    if (timeSettings.fbaMultiplier) setFbaVolumeMultiplier(timeSettings.fbaMultiplier);
    if (Object.keys(feeSettingsData).length > 0) setFeeSettings(feeSettingsData);
  };


  // Recharger les prix quand stockData arrive (composant recréé)
  useEffect(() => {
    if (stockData && stockData.length > 0) {
      // Vérifier si les prix ne sont pas déjà chargés
      if (Object.keys(purchasePrices).length === 0 || Object.keys(sellingPricesFBM).length === 0) {
        const savedPrices = localStorage.getItem('roiPurchasePrices');
        const savedSellingPricesFBM = localStorage.getItem('roiSellingPricesFBM');
        const savedSellingPricesFBA = localStorage.getItem('roiSellingPricesFBA');
        
        if (savedPrices && Object.keys(purchasePrices).length === 0) {
          try {
            const prices = JSON.parse(savedPrices);
            if (Object.keys(prices).length > 0) {
              setPurchasePrices(prices);
            }
          } catch (e) {
            console.error('Erreur rechargement prix d\'achat:', e);
          }
        }
        
        if (savedSellingPricesFBM && Object.keys(sellingPricesFBM).length === 0) {
          try {
            const prices = JSON.parse(savedSellingPricesFBM);
            if (Object.keys(prices).length > 0) {
              setSellingPricesFBM(prices);
            }
          } catch (e) {
            console.error('Erreur rechargement prix FBM:', e);
          }
        }
        
        if (savedSellingPricesFBA && Object.keys(sellingPricesFBA).length === 0) {
          try {
            const prices = JSON.parse(savedSellingPricesFBA);
            if (Object.keys(prices).length > 0) {
              setSellingPricesFBA(prices);
            }
          } catch (e) {
            console.error('Erreur rechargement prix FBA:', e);
          }
        }
      }
    }
  }, [stockData, purchasePrices, sellingPricesFBM, sellingPricesFBA]);

  // Sauvegarder les paramètres via API
  const saveSettings = async () => {
    try {
      // Sauvegarder paramètres temps
      await fetch('/api/roi/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timeSettings: JSON.stringify({
            fbm: timeSettingsFBM,
            fba: timeSettingsFBA,
            fbaMultiplier: fbaVolumeMultiplier
          })
        }),
      });

      // Sauvegarder paramètres frais
      await fetch('/api/roi/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feeSettings: JSON.stringify(feeSettings)
        }),
      });

      console.log('Paramètres ROI sauvegardés avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres ROI:', error);
    }
  };

  // Sauvegarder un prix via API
  const savePriceToAPI = async (sku, priceType, price) => {
    try {
      await fetch(`/api/prices/${sku}/${priceType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: parseFloat(price) || 0 }),
      });
    } catch (error) {
      console.error(`Erreur sauvegarde prix ${priceType} pour ${sku}:`, error);
    }
  };

  // Obtenir le délai du fournisseur
  const getSupplierLeadTime = (sku) => {
    if (!supplierConfig?.productSuppliers?.[sku] || !supplierConfig?.suppliers) return 14;
    
    const supplierId = parseInt(supplierConfig.productSuppliers[sku]);
    const supplier = supplierConfig.suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.leadTime : 14;
  };

  // Calculer le capital immobilisé pour un produit
  const calculateCapitalRequired = (item) => {
    const sku = item.sku;
    const dailySales = parseFloat(item.dailySales) || 0.1;
    const leadTime = getSupplierLeadTime(sku);
    const purchasePrice = purchasePrices[sku] || 0;
    
    if (purchasePrice === 0) return 0;
    
    // Stock actuel
    const currentStock = (parseInt(item.fbmQuantity) || 0) + 
                        (parseInt(item.fbaQuantity) || 0) + 
                        (parseInt(item.homeQuantity) || 0);
    
    // Stock en transit
    const transitStock = parseInt(item.transitQuantity) || 0;
    
    // Commande standard (délai + sécurité + 30 jours)
    const standardOrderUnits = Math.ceil(dailySales * (leadTime + safetyStock + 30));
    
    // Capital total nécessaire : stock actuel + stock transit + une commande standard
    const totalUnitsToFinance = currentStock + transitStock + standardOrderUnits;
    
    return totalUnitsToFinance * purchasePrice;
  };

  // Calculer les frais supplémentaires
  const calculateAdditionalFees = (sellingPrice, mode) => {
    let totalAdditionalFees = 0;
    
    feeSettings.additionalFees.forEach(fee => {
      const amount = mode === 'fbm' ? fee.fbmAmount : fee.fbaAmount;
      if (fee.unit === '€') {
        totalAdditionalFees += amount;
      } else if (fee.unit === '%') {
        totalAdditionalFees += (sellingPrice * amount / 100);
      }
    });
    
    return totalAdditionalFees;
  };

  // Calculer les marges FBM et FBA
  const calculateMargins = (item) => {
    const sku = item.sku;
    const purchasePrice = purchasePrices[sku] || 0;
    const sellingPriceFBM_TTC = sellingPricesFBM[sku] || 0;
    const sellingPriceFBA_TTC = sellingPricesFBA[sku] || 0;
    
    if (purchasePrice === 0) return { fbm: { margin: 0, percent: 0 }, fba: { margin: 0, percent: 0 } };
    
    // Calcul marge FBM
    let fbmMargin = 0;
    let fbmMarginPercent = 0;
    
    if (sellingPriceFBM_TTC > 0) {
      const sellingPriceFBM_HT = sellingPriceFBM_TTC / (1 + feeSettings.tvaRate / 100);
      const tvaAmountFBM = sellingPriceFBM_TTC - sellingPriceFBM_HT;
      const fbmCommission = sellingPriceFBM_TTC * feeSettings.amazonCommission / 100;
      const fbmDigitalServicesFee = fbmCommission * feeSettings.digitalServicesFee / 100;
      const fbmAdditionalFees = calculateAdditionalFees(sellingPriceFBM_TTC, 'fbm');
      const fbmCosts = purchasePrice + fbmCommission + fbmDigitalServicesFee + feeSettings.fbmShipping + fbmAdditionalFees + tvaAmountFBM;
      fbmMargin = sellingPriceFBM_TTC - fbmCosts;
      fbmMarginPercent = fbmMargin / sellingPriceFBM_HT * 100; // CORRECTION: division par prix HT
    }
    
    // Calcul marge FBA
    let fbaMargin = 0;
    let fbaMarginPercent = 0;
    
    if (sellingPriceFBA_TTC > 0) {
      const sellingPriceFBA_HT = sellingPriceFBA_TTC / (1 + feeSettings.tvaRate / 100);
      const tvaAmountFBA = sellingPriceFBA_TTC - sellingPriceFBA_HT;
      const fbaCommission = sellingPriceFBA_TTC * feeSettings.amazonCommission / 100;
      const fbaDigitalServicesFee = (fbaCommission + feeSettings.fbaFees) * feeSettings.digitalServicesFee / 100;
      const fbaAdditionalFees = calculateAdditionalFees(sellingPriceFBA_TTC, 'fba');
      const fbaCosts = purchasePrice + fbaCommission + feeSettings.fbaFees + fbaDigitalServicesFee + fbaAdditionalFees + tvaAmountFBA;
      fbaMargin = sellingPriceFBA_TTC - fbaCosts;
      fbaMarginPercent = fbaMargin / sellingPriceFBA_HT * 100; // CORRECTION: division par prix HT
    }
    
    return {
      fbm: { margin: fbmMargin, percent: fbmMarginPercent },
      fba: { margin: fbaMargin, percent: fbaMarginPercent }
    };
  };

  // Calculatrice de test
  const calculateTestMargins = () => {
    if (!testPurchasePrice || testPurchasePrice === 0 || isNaN(testPurchasePrice)) {
      return { 
        fbm: { margin: 0, percent: 0, priceHT: 0 }, 
        fba: { margin: 0, percent: 0, priceHT: 0 } 
      };
    }
    
    // Vérifier que les prix de vente sont valides
    const validSellingPriceFBM = testSellingPriceFBM && !isNaN(testSellingPriceFBM) ? testSellingPriceFBM : 0;
    const validSellingPriceFBA = testSellingPriceFBA && !isNaN(testSellingPriceFBA) ? testSellingPriceFBA : 0;
    
    // Les prix de vente sont déjà TTC, on calcule le HT
    const testSellingPriceFBM_HT = validSellingPriceFBM / (1 + feeSettings.tvaRate / 100);
    const testSellingPriceFBA_HT = validSellingPriceFBA / (1 + feeSettings.tvaRate / 100);
    const tvaAmountFBM = validSellingPriceFBM - testSellingPriceFBM_HT;
    const tvaAmountFBA = validSellingPriceFBA - testSellingPriceFBA_HT;
    
    // Calcul marge FBM test avec frais de service numérique sur commission + frais supplémentaires
    const fbmCommission = validSellingPriceFBM * feeSettings.amazonCommission / 100;
    const fbmDigitalServicesFee = fbmCommission * feeSettings.digitalServicesFee / 100;
    const fbmAdditionalFees = calculateAdditionalFees(validSellingPriceFBM, 'fbm');
    const fbmCosts = testPurchasePrice + fbmCommission + fbmDigitalServicesFee + feeSettings.fbmShipping + fbmAdditionalFees + tvaAmountFBM;
    const fbmMargin = validSellingPriceFBM - fbmCosts;
    const fbmMarginPercent = testSellingPriceFBM_HT > 0 ? fbmMargin / testSellingPriceFBM_HT * 100 : 0;
    
    // Calcul marge FBA test avec frais de service numérique sur commission + frais FBA + frais supplémentaires
    const fbaCommission = validSellingPriceFBA * feeSettings.amazonCommission / 100;
    const fbaDigitalServicesFee = (fbaCommission + feeSettings.fbaFees) * feeSettings.digitalServicesFee / 100;
    const fbaAdditionalFees = calculateAdditionalFees(validSellingPriceFBA, 'fba');
    const fbaCosts = testPurchasePrice + fbaCommission + feeSettings.fbaFees + fbaDigitalServicesFee + fbaAdditionalFees + tvaAmountFBA;
    const fbaMargin = validSellingPriceFBA - fbaCosts;
    const fbaMarginPercent = testSellingPriceFBA_HT > 0 ? fbaMargin / testSellingPriceFBA_HT * 100 : 0;
    
    return {
      fbm: { margin: fbmMargin, percent: fbmMarginPercent, priceHT: testSellingPriceFBM_HT },
      fba: { margin: fbaMargin, percent: fbaMarginPercent, priceHT: testSellingPriceFBA_HT }
    };
  };

  const testMargins = calculateTestMargins();

  // Calculer le ROI annualisé
  const calculateROI = (item) => {
    const dailySales = parseFloat(item.dailySales) || 0.1;
    const capitalRequired = calculateCapitalRequired(item);
    const margins = calculateMargins(item);
    
    if (capitalRequired === 0 || dailySales === 0) return { fbm: 0, fba: 0 };
    
    // Profit annuel FBM = marge unitaire × ventes annuelles actuelles
    const annualSalesFBM = dailySales * 365;
    const annualProfitFBM = margins.fbm.margin * annualSalesFBM;
    
    // Profit annuel FBA = marge unitaire × ventes annuelles × multiplicateur
    const annualSalesFBA = dailySales * 365 * fbaVolumeMultiplier;
    const annualProfitFBA = margins.fba.margin * annualSalesFBA;
    
    // ROI = profit annuel / capital immobilisé
    const roiFBM = (annualProfitFBM / capitalRequired) * 100;
    const roiFBA = (annualProfitFBA / capitalRequired) * 100;
    
    return { fbm: roiFBM, fba: roiFBA };
  };

  // Calculer la rentabilité horaire
  const calculateHourlyProfitability = (item) => {
    const dailySales = parseFloat(item.dailySales) || 0.1;
    const margins = calculateMargins(item);
    
    // Temps nécessaire par 1000 unités
    const timeFBMper1000 = timeSettingsFBM; // minutes
    const timeFBAper1000 = timeSettingsFBA; // minutes
    
    // Temps par unité
    const timeFBMperUnit = timeFBMper1000 / 1000; // minutes par unité
    const timeFBAperUnit = timeFBAper1000 / 1000; // minutes par unité
    
    // Ventes quotidiennes ajustées
    const dailySalesFBM = dailySales;
    const dailySalesFBA = dailySales * fbaVolumeMultiplier;
    
    // Temps quotidien nécessaire
    const dailyTimeFBM = (dailySalesFBM * timeFBMperUnit) / 60; // heures
    const dailyTimeFBA = (dailySalesFBA * timeFBAperUnit) / 60; // heures
    
    // Profit quotidien
    const dailyProfitFBM = dailySalesFBM * margins.fbm.margin;
    const dailyProfitFBA = dailySalesFBA * margins.fba.margin;
    
    // Rentabilité horaire
    const hourlyProfitFBM = dailyTimeFBM > 0 ? dailyProfitFBM / dailyTimeFBM : 0;
    const hourlyProfitFBA = dailyTimeFBA > 0 ? dailyProfitFBA / dailyTimeFBA : 0;
    
    return {
      fbm: hourlyProfitFBM,
      fba: hourlyProfitFBA,
      dailyTimeFBM: dailyTimeFBM,
      dailyTimeFBA: dailyTimeFBA
    };
  };

  // Recalcul automatique avec délai (sans boucle infinie)
  useEffect(() => {
    if (recalcTimer.current) {
      clearTimeout(recalcTimer.current);
    }
    
    recalcTimer.current = setTimeout(() => {
      if (!stockData || stockData.length === 0) return;
      
      const newROIData = stockData.map(item => {
        const margins = calculateMargins(item);
        const capital = calculateCapitalRequired(item);
        const roi = calculateROI(item);
        const hourly = calculateHourlyProfitability(item);
        
        // Recommandation basée sur ROI et rentabilité horaire
        let recommendation = '';
        if (roi.fbm > roi.fba && hourly.fbm > hourly.fba) {
          recommendation = 'FBM';
        } else if (roi.fba > roi.fbm && hourly.fba > hourly.fbm) {
          recommendation = 'FBA';
        } else if (hourly.fba > hourly.fbm * 2) {
          recommendation = 'FBA (temps)';
        } else {
          recommendation = 'Analyser';
        }
        
        return {
          sku: item.sku,
          productName: item.productName || '',
          dailySales: parseFloat(item.dailySales) || 0,
          purchasePrice: purchasePrices[item.sku] || 0,
          sellingPriceFBM: sellingPricesFBM[item.sku] || 0,
          sellingPriceFBA: sellingPricesFBA[item.sku] || 0,
          capitalRequired: capital,
          marginFBM: margins.fbm.margin,
          marginFBA: margins.fba.margin,
          marginPercentFBM: margins.fbm.percent,
          marginPercentFBA: margins.fba.percent,
          roiAnnualFBM: roi.fbm,
          roiAnnualFBA: roi.fba,
          hourlyProfitFBM: hourly.fbm,
          hourlyProfitFBA: hourly.fba,
          dailyTimeFBM: hourly.dailyTimeFBM,
          dailyTimeFBA: hourly.dailyTimeFBA,
          recommendation
        };
      });
      
      setRoiData(newROIData);
    }, 2000);

    return () => {
      if (recalcTimer.current) {
        clearTimeout(recalcTimer.current);
      }
    };
  }, [stockData, purchasePrices, sellingPricesFBM, sellingPricesFBA, feeSettings, timeSettingsFBM, timeSettingsFBA, fbaVolumeMultiplier]);

  // Gestion du tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filtrer et trier les données
  const filteredData = roiData.filter(item => 
    item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.productName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedData = [...filteredData].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];
    
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    }
    
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    }
    
    return 0;
  });

  // Mettre à jour le prix d'achat
  const updatePurchasePrice = async (sku, price) => {
    const numericPrice = parseFloat(price) || 0;
    const newPrices = { ...purchasePrices, [sku]: numericPrice };
    setPurchasePrices(newPrices);
    // Sauvegarde immédiate via API
    await savePriceToAPI(sku, 'purchase', numericPrice);
  };

  // Mettre à jour le prix de vente FBM
  const updateSellingPriceFBM = async (sku, price) => {
    const numericPrice = parseFloat(price) || 0;
    const newPrices = { ...sellingPricesFBM, [sku]: numericPrice };
    setSellingPricesFBM(newPrices);
    // Sauvegarde immédiate via API
    await savePriceToAPI(sku, 'selling_fbm', numericPrice);
  };

  // Mettre à jour le prix de vente FBA
  const updateSellingPriceFBA = async (sku, price) => {
    const numericPrice = parseFloat(price) || 0;
    const newPrices = { ...sellingPricesFBA, [sku]: numericPrice };
    setSellingPricesFBA(newPrices);
    // Sauvegarde immédiate via API
    await savePriceToAPI(sku, 'selling_fba', numericPrice);
  };

  // Ajouter un frais supplémentaire
  const addAdditionalFee = () => {
    const newFee = {
      id: Date.now(),
      name: 'Nouveau frais',
      fbmAmount: 0,
      fbaAmount: 0,
      unit: '€' // ou '%'
    };
    
    const newSettings = {
      ...feeSettings,
      additionalFees: [...feeSettings.additionalFees, newFee]
    };
    setFeeSettings(newSettings);
    localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
  };

  // Supprimer un frais supplémentaire
  const removeAdditionalFee = (feeId) => {
    const newSettings = {
      ...feeSettings,
      additionalFees: feeSettings.additionalFees.filter(fee => fee.id !== feeId)
    };
    setFeeSettings(newSettings);
    localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
  };

  // Modifier un frais supplémentaire
  const updateAdditionalFee = (feeId, field, value) => {
    const newSettings = {
      ...feeSettings,
      additionalFees: feeSettings.additionalFees.map(fee => 
        fee.id === feeId ? { ...fee, [field]: value } : fee
      )
    };
    setFeeSettings(newSettings);
    localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
  };

  if (!stockData || stockData.length === 0) {
    return (
      <div className="roi-analysis">
        <h2>Analyse ROI</h2>
        <p>Aucune donnée de stock disponible. Veuillez d'abord importer vos données.</p>
      </div>
    );
  }

  return (
    <div className="roi-analysis">
      <h2>Analyse ROI et Rentabilité</h2>
      
      {/* Section Paramètres */}
      <div className="roi-settings">
        <h3>Paramètres Globaux</h3>
        
        <div className="settings-grid">
          <div className="setting-group">
            <label>Temps FBM (min/1000 unités):</label>
            <input
              type="number"
              value={timeSettingsFBM}
              onChange={(e) => {
                const newValue = parseInt(e.target.value) || 0;
                setTimeSettingsFBM(newValue);
              }}
              min="0"
            />
          </div>
          
          <div className="setting-group">
            <label>Temps FBA (min/1000 unités):</label>
            <input
              type="number"
              value={timeSettingsFBA}
              onChange={(e) => {
                const newValue = parseInt(e.target.value) || 0;
                setTimeSettingsFBA(newValue);
              }}
              min="0"
            />
          </div>
          
          <div className="setting-group">
            <label>Multiplicateur volume FBA:</label>
            <input
              type="number"
              value={fbaVolumeMultiplier}
              onChange={(e) => {
                const newValue = parseFloat(e.target.value) || 1.0;
                setFbaVolumeMultiplier(newValue);
              }}
              min="0.1"
              step="0.1"
            />
            <div className="help-text">Combien de fois plus vous vendez en FBA vs FBM</div>
          </div>
        </div>
      </div>

      {/* Section Frais et Calculatrice */}
      <div className="fee-settings">
        <h3>Configuration des Frais & Calculatrice de Marge</h3>
        
        <div className="fee-grid">
          <div className="fee-group">
            <label>Commission Amazon (%):</label>
            <input
              type="number"
              value={feeSettings.amazonCommission}
              onChange={(e) => {
                const newSettings = {
                  ...feeSettings,
                  amazonCommission: parseFloat(e.target.value) || 0
                };
                setFeeSettings(newSettings);
                localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
              }}
              min="0"
              step="0.1"
            />
          </div>
          
          <div className="fee-group">
            <label>Frais FBA (€/unité):</label>
            <input
              type="number"
              value={feeSettings.fbaFees}
              onChange={(e) => {
                const newSettings = {
                  ...feeSettings,
                  fbaFees: parseFloat(e.target.value) || 0
                };
                setFeeSettings(newSettings);
                localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
              }}
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="fee-group">
            <label>Expédition FBM (€/unité):</label>
            <input
              type="number"
              value={feeSettings.fbmShipping}
              onChange={(e) => {
                const newSettings = {
                  ...feeSettings,
                  fbmShipping: parseFloat(e.target.value) || 0
                };
                setFeeSettings(newSettings);
                localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
              }}
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="fee-group">
            <label>Frais service numérique (%):</label>
            <input
              type="number"
              value={feeSettings.digitalServicesFee}
              onChange={(e) => {
                const newSettings = {
                  ...feeSettings,
                  digitalServicesFee: parseFloat(e.target.value) || 0
                };
                setFeeSettings(newSettings);
                localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
              }}
              min="0"
              step="0.1"
            />
            <div className="help-text">Sur (commission + frais FBA)</div>
          </div>
          
          <div className="fee-group">
            <label>TVA (%):</label>
            <input
              type="number"
              value={feeSettings.tvaRate}
              onChange={(e) => {
                const newSettings = {
                  ...feeSettings,
                  tvaRate: parseFloat(e.target.value) || 0
                };
                setFeeSettings(newSettings);
                localStorage.setItem('roiFeeSettings', JSON.stringify(newSettings));
              }}
              min="0"
              step="0.1"
            />
            <div className="help-text">Prix HT → Prix TTC pour calcul commission</div>
          </div>
        </div>
        
        {/* Frais additionnels */}
        {feeSettings.additionalFees && feeSettings.additionalFees.length > 0 && (
          <div className="additional-fees-section">
            <h4>Frais Supplémentaires</h4>
            <div className="additional-fees-header">
              <div className="header-name">Nom du frais</div>
              <div className="header-fbm">Montant FBM</div>
              <div className="header-fba">Montant FBA</div>
              <div className="header-unit">Unité</div>
              <div className="header-action">Action</div>
            </div>
            {feeSettings.additionalFees.map((fee) => (
              <div key={fee.id} className="additional-fee-item">
                <input
                  type="text"
                  value={fee.name}
                  onChange={(e) => updateAdditionalFee(fee.id, 'name', e.target.value)}
                  placeholder="Ex: Assurance, Stockage..."
                  className="fee-name-input"
                />
                <input
                  type="number"
                  value={fee.fbmAmount}
                  onChange={(e) => updateAdditionalFee(fee.id, 'fbmAmount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="fee-amount-input"
                  step="0.01"
                />
                <input
                  type="number"
                  value={fee.fbaAmount}
                  onChange={(e) => updateAdditionalFee(fee.id, 'fbaAmount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="fee-amount-input"
                  step="0.01"
                />
                <select
                  value={fee.unit}
                  onChange={(e) => updateAdditionalFee(fee.id, 'unit', e.target.value)}
                  className="fee-unit-select"
                >
                  <option value="€">€</option>
                  <option value="%">%</option>
                </select>
                <button 
                  onClick={() => removeAdditionalFee(fee.id)}
                  className="remove-fee-btn"
                  title="Supprimer ce frais"
                >
                  ✖
                </button>
              </div>
            ))}
          </div>
        )}
        
        <button onClick={addAdditionalFee} className="add-fee-btn">
          Ajouter un frais
        </button>
        
        <button onClick={saveSettings} className="save-settings-btn">
          💾 Sauvegarder maintenant
        </button>
        
        {/* Calculatrice de test */}
        <div className="calculator-section">
          <h4>🧮 Calculatrice de Test</h4>
          <div className="calculator-grid">
            <div className="calculator-inputs">
              <div className="calc-input-group">
                <label>Prix d'achat (€):</label>
                <input
                  type="number"
                  value={testPurchasePrice}
                  onChange={(e) => setTestPurchasePrice(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  className="calc-input"
                />
              </div>
              
              <div className="calc-input-group">
                <label>Prix de vente FBM TTC (€):</label>
                <input
                  type="number"
                  value={testSellingPriceFBM}
                  onChange={(e) => setTestSellingPriceFBM(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  className="calc-input fbm-input"
                />
                <div className="help-text">→ HT: {(testSellingPriceFBM && !isNaN(testSellingPriceFBM) ? (testSellingPriceFBM / (1 + feeSettings.tvaRate / 100)) : 0).toFixed(2)}€</div>
              </div>
              
              <div className="calc-input-group">
                <label>Prix de vente FBA TTC (€):</label>
                <input
                  type="number"
                  value={testSellingPriceFBA}
                  onChange={(e) => setTestSellingPriceFBA(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  className="calc-input fba-input"
                />
                <div className="help-text">→ HT: {(testSellingPriceFBA && !isNaN(testSellingPriceFBA) ? (testSellingPriceFBA / (1 + feeSettings.tvaRate / 100)) : 0).toFixed(2)}€</div>
              </div>
            </div>
            
            <div className="calculator-results">
              <div className="result-item fbm-result">
                <div className="result-label">📦 FBM</div>
                <div className="result-value">
                  {(testMargins.fbm.margin || 0).toFixed(2)}€
                  <span className="result-percent">({(testMargins.fbm.percent || 0).toFixed(1)}%)</span>
                </div>
                <div className="result-price">Prix HT: {(testMargins.fbm.priceHT || 0).toFixed(2)}€</div>
              </div>
              
              <div className="result-item fba-result">
                <div className="result-label">🏢 FBA</div>
                <div className="result-value">
                  {(testMargins.fba.margin || 0).toFixed(2)}€
                  <span className="result-percent">({(testMargins.fba.percent || 0).toFixed(1)}%)</span>
                </div>
                <div className="result-price">Prix HT: {(testMargins.fba.priceHT || 0).toFixed(2)}€</div>
              </div>
              
              <div className="result-item difference-result">
                <div className="result-label">⚖️ Différence</div>
                <div className="result-value">
                  {((testMargins.fba.margin || 0) - (testMargins.fbm.margin || 0)).toFixed(2)}€
                  <span className="result-percent">
                    ({((testMargins.fba.margin || 0) > (testMargins.fbm.margin || 0) ? '+' : '')}{((testMargins.fba.margin || 0) - (testMargins.fbm.margin || 0)).toFixed(2)}€)
                  </span>
                </div>
                <div className="result-price">
                  {(testMargins.fba.margin || 0) > (testMargins.fbm.margin || 0) ? 'FBA gagne' : 
                   (testMargins.fba.margin || 0) < (testMargins.fbm.margin || 0) ? 'FBM gagne' : 'Égalité'}
                </div>
              </div>
            </div>
          </div>
          
          {/* Détail des calculs */}
          <div className="calculation-details">
            <div className="detail-column">
              <h5>Détail FBM</h5>
              <div className="detail-line">Prix TTC: {(testSellingPriceFBM || 0).toFixed(2)}€</div>
              <div className="detail-line">Prix HT: {(testMargins.fbm.priceHT || 0).toFixed(2)}€</div>
              <div className="detail-line">TVA ({feeSettings.tvaRate}%): {((testSellingPriceFBM || 0) - (testMargins.fbm.priceHT || 0)).toFixed(2)}€</div>
              <div className="detail-line">Commission ({feeSettings.amazonCommission}%): -{((testSellingPriceFBM || 0) * feeSettings.amazonCommission / 100).toFixed(2)}€</div>
              <div className="detail-line">Service numérique ({feeSettings.digitalServicesFee}%): -{(((testSellingPriceFBM || 0) * feeSettings.amazonCommission / 100) * feeSettings.digitalServicesFee / 100).toFixed(2)}€</div>
              <div className="detail-line">Expédition: -{feeSettings.fbmShipping.toFixed(2)}€</div>
              {feeSettings.additionalFees.map((fee) => (
                <div key={fee.id} className="detail-line">
                  {fee.name}: -{fee.unit === '€' ? fee.fbmAmount.toFixed(2) : ((testSellingPriceFBM || 0) * fee.fbmAmount / 100).toFixed(2)}€
                </div>
              ))}
              <div className="detail-line">Prix d'achat: -{(testPurchasePrice || 0).toFixed(2)}€</div>
              <div className="detail-line total">Marge: {(testMargins.fbm.margin || 0).toFixed(2)}€</div>
            </div>
            
            <div className="detail-column">
              <h5>Détail FBA</h5>
              <div className="detail-line">Prix TTC: {(testSellingPriceFBA || 0).toFixed(2)}€</div>
              <div className="detail-line">Prix HT: {(testMargins.fba.priceHT || 0).toFixed(2)}€</div>
              <div className="detail-line">TVA ({feeSettings.tvaRate}%): {((testSellingPriceFBA || 0) - (testMargins.fba.priceHT || 0)).toFixed(2)}€</div>
              <div className="detail-line">Commission ({feeSettings.amazonCommission}%): -{((testSellingPriceFBA || 0) * feeSettings.amazonCommission / 100).toFixed(2)}€</div>
              <div className="detail-line">Frais FBA: -{feeSettings.fbaFees.toFixed(2)}€</div>
              <div className="detail-line">Service numérique ({feeSettings.digitalServicesFee}%): -{(((testSellingPriceFBA || 0) * feeSettings.amazonCommission / 100 + feeSettings.fbaFees) * feeSettings.digitalServicesFee / 100).toFixed(2)}€</div>
              {feeSettings.additionalFees.map((fee) => (
                <div key={fee.id} className="detail-line">
                  {fee.name}: -{fee.unit === '€' ? fee.fbaAmount.toFixed(2) : ((testSellingPriceFBA || 0) * fee.fbaAmount / 100).toFixed(2)}€
                </div>
              ))}
              <div className="detail-line">Prix d'achat: -{(testPurchasePrice || 0).toFixed(2)}€</div>
              <div className="detail-line total">Marge: {(testMargins.fba.margin || 0).toFixed(2)}€</div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Tableau */}
      <div className="roi-table-section">
        <div className="table-controls">
          <input
            type="text"
            placeholder="Rechercher par SKU ou nom..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="table-wrapper">
          <table className="roi-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('sku')}>
                  SKU {sortField === 'sku' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th>Prix d'achat (€)</th>
                <th>Prix de vente FBM TTC (€)</th>
                <th>Prix de vente FBA TTC (€)</th>
                <th onClick={() => handleSort('capitalRequired')}>
                  Capital Immobilisé (€) {sortField === 'capitalRequired' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('roiAnnualFBM')}>
                  ROI FBM (%) {sortField === 'roiAnnualFBM' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('roiAnnualFBA')}>
                  ROI FBA (%) {sortField === 'roiAnnualFBA' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('hourlyProfitFBM')}>
                  €/h FBM {sortField === 'hourlyProfitFBM' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('hourlyProfitFBA')}>
                  €/h FBA {sortField === 'hourlyProfitFBA' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th>Recommandation</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item) => (
                <tr key={item.sku} className="roi-row">
                  <td>{item.sku}</td>
                  <td>
                    <input
                      type="number"
                      value={purchasePrices[item.sku] || 0}
                      onChange={(e) => updatePurchasePrice(item.sku, e.target.value)}
                      min="0"
                      step="0.01"
                      className="price-input"
                      placeholder="0.00"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={sellingPricesFBM[item.sku] || 0}
                      onChange={(e) => updateSellingPriceFBM(item.sku, e.target.value)}
                      min="0"
                      step="0.01"
                      className="price-input"
                      placeholder="0.00"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={sellingPricesFBA[item.sku] || 0}
                      onChange={(e) => updateSellingPriceFBA(item.sku, e.target.value)}
                      min="0"
                      step="0.01"
                      className="price-input"
                      placeholder="0.00"
                    />
                  </td>
                  <td className="capital-cell">
                    {item.capitalRequired.toFixed(0)} €
                  </td>
                  <td className={`roi-cell ${item.roiAnnualFBM > 100 ? 'excellent' : item.roiAnnualFBM > 50 ? 'good' : item.roiAnnualFBM < 0 ? 'poor' : ''}`}>
                    {item.roiAnnualFBM.toFixed(0)}%
                  </td>
                  <td className={`roi-cell ${item.roiAnnualFBA > 100 ? 'excellent' : item.roiAnnualFBA > 50 ? 'good' : item.roiAnnualFBA < 0 ? 'poor' : ''}`}>
                    {item.roiAnnualFBA.toFixed(0)}%
                  </td>
                  <td className="hourly-cell">
                    {item.hourlyProfitFBM.toFixed(0)} €/h
                    <div className="time-info">({item.dailyTimeFBM.toFixed(1)}h/jour)</div>
                  </td>
                  <td className="hourly-cell">
                    {item.hourlyProfitFBA.toFixed(0)} €/h
                    <div className="time-info">({item.dailyTimeFBA.toFixed(1)}h/jour)</div>
                  </td>
                  <td className={`recommendation ${item.recommendation.toLowerCase().replace(' ', '-')}`}>
                    {item.recommendation}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Résumé */}
      <div className="roi-summary">
        <h3>Résumé Global</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <label>Capital Total Immobilisé:</label>
            <span className="summary-value">
              {sortedData.reduce((sum, item) => sum + item.capitalRequired, 0).toFixed(0)} €
            </span>
          </div>
          <div className="summary-item">
            <label>ROI Moyen FBM:</label>
            <span className="summary-value">
              {sortedData.length > 0 ? (sortedData.reduce((sum, item) => sum + item.roiAnnualFBM, 0) / sortedData.length).toFixed(0) : 0}%
            </span>
          </div>
          <div className="summary-item">
            <label>ROI Moyen FBA:</label>
            <span className="summary-value">
              {sortedData.length > 0 ? (sortedData.reduce((sum, item) => sum + item.roiAnnualFBA, 0) / sortedData.length).toFixed(0) : 0}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ROIAnalysis;